import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowUturnLeftIcon, ArrowRightCircleIcon } from '@heroicons/react/24/outline';
import API from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { formatIsoDate } from '../utils/formatDateValue';
import { enumLabel } from '../utils/enumLabel';

/**
 * Shown at the top of an ARCHIVED case that was merged into another: "Merged into IPRI-2026-#### on
 * <date> by <user>", a link, and Un-merge for APPROVER/ADMIN.
 *
 * Newer merges carry everything in `metadata.merge` + `mergedInto` from GET /api/cases/:id. Older
 * ones (archived before those existed) have only their CONFIRMED match, so the banner falls back to
 * GET /api/dedup/matches/:caseId and reads the canonical and the match id from there. If neither
 * yields a canonical the case was archived for some other reason and the banner renders nothing.
 */

interface MergeMeta {
  at?: string;
  by?: { id?: string; name?: string } | null;
  matchId?: string;
  canonicalCaseId?: string;
}

interface MatchRow {
  id: string;
  status: string;
  caseId: string;
  matchedCaseId: string;
  createdAt: string;
  reviewedAt?: string | null;
  autoConfirmed?: boolean;
  reviewedBy?: { firstName: string; lastName: string } | null;
  matchedCase?: { id: string; caseNumber: string } | null;
}

interface UnmergeResult {
  caseNumber?: string;
  canonicalCaseNumber?: string;
  restoredStatus?: string;
  withdrawnUpdates?: number;
  fieldsRestored?: string[];
  fieldsKept?: string[];
}

interface Props {
  caseId: string;
  caseNumber: string;
  mergedInto?: { id: string; caseNumber: string } | null;
  merge?: MergeMeta | null;
  canUnmerge: boolean;
  onUnmerged: () => void;
}

const MergedIntoBanner: React.FC<Props> = ({ caseId, caseNumber, mergedInto, merge, canUnmerge, onUnmerged }) => {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();

  const needLookup = !merge?.matchId || !mergedInto;
  const matches = useQuery<MatchRow[]>({
    queryKey: ['case-matches', caseId],
    queryFn: async () => (await API.get(`/api/dedup/matches/${caseId}`)).data,
    enabled: needLookup,
  });
  const confirmed = (matches.data || []).find((m) => m.status === 'CONFIRMED' && m.caseId === caseId) || null;

  const canonical = mergedInto ?? confirmed?.matchedCase ?? null;
  const matchId = merge?.matchId ?? confirmed?.id ?? null;
  const at = merge?.at ?? confirmed?.reviewedAt ?? confirmed?.createdAt ?? null;
  const by = merge?.by?.name
    ?? (confirmed?.reviewedBy ? `${confirmed.reviewedBy.firstName} ${confirmed.reviewedBy.lastName}` : null)
    ?? (confirmed?.autoConfirmed || (merge && !merge.by) ? 'the duplicate check' : null);

  const unmerge = useMutation({
    mutationFn: async (id: string) => (await API.post<UnmergeResult>(`/api/dedup/matches/${id}/unmerge`)).data,
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: ['case-matches', caseId] });
      queryClient.invalidateQueries({ queryKey: ['dedup-archived'] });
      queryClient.invalidateQueries({ queryKey: ['dedup-pending-updates'] });
      const withdrawn = d?.withdrawnUpdates
        ? `; ${d.withdrawnUpdates} pending update${d.withdrawnUpdates === 1 ? '' : 's'} withdrawn from ${d.canonicalCaseNumber ?? 'the original'}`
        : '';
      const restored = Array.isArray(d?.fieldsRestored) && d.fieldsRestored.length ? `; ${d.fieldsRestored.length} field${d.fieldsRestored.length === 1 ? '' : 's'} put back on ${d.canonicalCaseNumber ?? 'the original'}` : '';
      const keptF = Array.isArray(d?.fieldsKept) && d.fieldsKept.length ? ` (${d.fieldsKept.length} kept: edited since the merge)` : '';
      showToast('success', `Un-merged — ${d?.caseNumber ?? caseNumber} restored${d?.restoredStatus ? ` as ${enumLabel(d.restoredStatus)}` : ''}${withdrawn}${restored}${keptF}`);
      onUnmerged();
    },
    onError: (err: any) => showToast('error', err?.response?.data?.error || 'Failed to un-merge'),
  });

  const handleUnmerge = async () => {
    if (!matchId) return;
    const ok = await confirm({
      title: 'Restore as its own case?',
      message: `Restore ${caseNumber} as its own case, with the status it had before the merge? Everything the merge put on ${canonical?.caseNumber ?? 'the original'} is taken back — copied rows, sources, documents, comments, and the fields you chose — except a field someone edited again since the merge.`,
      confirmLabel: 'Un-merge',
      cancelLabel: 'Cancel',
    });
    if (ok) unmerge.mutate(matchId);
  };

  if (!canonical && !matchId) return null; // archived for another reason, or still loading the fallback

  return (
    <div className="mb-4 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" role="status">
      <ArrowRightCircleIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-[16rem] text-sm text-gray-800 dark:text-gray-100">
        <span className="font-medium">
          Merged into {canonical ? <Link to={`/cases/${canonical.id}`} className="text-primary hover:underline">{canonical.caseNumber}</Link> : 'another case'}
        </span>
        {at && <> on {formatIsoDate(at)}</>}
        {by && <> by {by}</>}.
        <span className="block text-xs text-gray-600 dark:text-gray-400 mt-0.5">This record is kept for reference and no longer counts.</span>
      </div>
      <div className="flex items-center gap-2">
        {canonical && (
          <Link to={`/cases/${canonical.id}`} className="inline-flex items-center px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700">
            Open {canonical.caseNumber}
          </Link>
        )}
        {canUnmerge && matchId && (
          <button
            type="button"
            disabled={unmerge.isPending}
            onClick={handleUnmerge}
            title="Restore as its own case and withdraw its pending updates"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <ArrowUturnLeftIcon className="h-3.5 w-3.5" aria-hidden="true" /> {unmerge.isPending ? 'Un-merging…' : 'Un-merge'}
          </button>
        )}
      </div>
    </div>
  );
};

export default MergedIntoBanner;
